import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Check, Code2, Copy, Download, Link2, Loader2, Mail, QrCode, Send, X,
} from 'lucide-react';
import { api } from '../../../lib/api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { detalleError } from './contexto';

const PESTANAS = [
  { id: 'correo', texto: 'Correo', Icono: Mail },
  { id: 'enlace', texto: 'Enlace', Icono: Link2 },
  { id: 'insertar', texto: 'Insertar', Icono: Code2 },
  { id: 'qr', texto: 'Código QR', Icono: QrCode },
];

const RE_CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

async function copiarTexto(texto, aviso = 'Copiado') {
  try {
    await navigator.clipboard.writeText(texto);
    toast.success(aviso);
    return true;
  } catch {
    window.prompt('Copia:', texto);
    return false;
  }
}

/**
 * "Enviar formulario": por correo (sale del buzón de quien envía, como cualquier correo suyo), enlace,
 * HTML para insertarlo en una web, código QR (para imprimirlo en una caseta o un cartel) y redes.
 * Sin publicar no hay nada que enviar: se ofrece publicar primero.
 */
export default function DialogoEnviar({ open, onOpenChange, formId, formulario, onPublicar }) {
  const pub = formulario?.publicacion || {};
  const url = pub.url_publica || '';
  const [pestana, setPestana] = useState('correo');

  if (!pub.publicado || !url) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="fe-dialogo max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Enviar formulario</DialogTitle>
            <DialogDescription>Primero publica el formulario: sin publicar, nadie puede responderlo aunque tenga el enlace.</DialogDescription>
          </DialogHeader>
          <div className="fe-dialogo-pie">
            <span className="flex-1" />
            <button type="button" className="fe-btn-borde" onClick={() => onOpenChange(false)}>Cancelar</button>
            <button type="button" className="fe-btn-primario" onClick={() => { onOpenChange(false); onPublicar(); }}>Publicar</button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="fe-dialogo fe-dialogo-enviar max-w-xl rounded-2xl">
        <DialogHeader>
          <DialogTitle>Enviar formulario</DialogTitle>
        </DialogHeader>
        <div className="fe-enviar-pestanas" role="tablist" aria-label="Forma de enviar">
          {PESTANAS.map(({ id, texto, Icono }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={pestana === id}
              className={`fe-enviar-pestana ${pestana === id ? 'is-activa' : ''}`}
              onClick={() => setPestana(id)}
            >
              <Icono /> <span>{texto}</span>
            </button>
          ))}
        </div>
        <div role="tabpanel" className="fe-enviar-panel">
          {pestana === 'correo' && <EnviarCorreo formId={formId} formulario={formulario} url={url} onEnviado={() => onOpenChange(false)} />}
          {pestana === 'enlace' && <EnviarEnlace url={url} titulo={formulario.titulo} />}
          {pestana === 'insertar' && <EnviarInsertar url={url} />}
          {pestana === 'qr' && <EnviarQr formId={formId} titulo={formulario.titulo} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EnviarCorreo({ formId, formulario, url, onEnviado }) {
  const [cuentas, setCuentas] = useState([]);
  const [cuenta, setCuenta] = useState('');
  const [para, setPara] = useState([]);
  const [escribiendo, setEscribiendo] = useState('');
  const [asunto, setAsunto] = useState(formulario.titulo || 'Formulario');
  const [mensaje, setMensaje] = useState(`Te invito a llenar este formulario: ${formulario.titulo || ''}`.trim());
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.get('/accounts').then(({ data }) => {
      if (!vivo) return;
      const lista = Array.isArray(data) ? data : [];
      setCuentas(lista);
      if (lista[0]) setCuenta(lista[0].id);
    }).catch(() => {});
    return () => { vivo = false; };
  }, []);

  const agregar = (texto) => {
    const nuevos = texto.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    const validos = nuevos.filter((c) => RE_CORREO.test(c));
    if (nuevos.length !== validos.length) toast.error('Alguna dirección no es válida');
    setPara((p) => [...new Set([...p, ...validos.map((c) => c.toLowerCase())])]);
    setEscribiendo('');
  };

  const enviar = async (e) => {
    e.preventDefault();
    const pendientes = escribiendo.trim() ? escribiendo.split(/[\s,;]+/).filter((c) => RE_CORREO.test(c)) : [];
    const destinatarios = [...new Set([...para, ...pendientes.map((c) => c.toLowerCase())])];
    if (!destinatarios.length) {
      toast.error('Añade al menos un destinatario');
      return;
    }
    setEnviando(true);
    try {
      const { data } = await api.post(`/forms/${formId}/enviar`, {
        para: destinatarios,
        asunto: asunto.trim() || formulario.titulo || 'Formulario',
        mensaje,
        ...(cuenta ? { account_id: cuenta } : {}),
      });
      toast.success(`Formulario enviado a ${data?.enviados ?? destinatarios.length} ${destinatarios.length === 1 ? 'persona' : 'personas'}`);
      onEnviado();
    } catch (err) {
      toast.error(detalleError(err, 'No se pudo enviar el correo'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form className="fe-enviar-correo" onSubmit={enviar} noValidate>
      {cuentas.length > 1 && (
        <label className="fe-campo-etiquetado">
          <span>De</span>
          <select className="fe-select" value={cuenta} onChange={(e) => setCuenta(e.target.value)} aria-label="Cuenta desde la que se envía">
            {cuentas.map((c) => <option key={c.id} value={c.id}>{c.display_name ? `${c.display_name} <${c.email}>` : c.email}</option>)}
          </select>
        </label>
      )}
      <div className="fe-campo-etiquetado">
        <span>Para</span>
        <div className="fe-chips">
          {para.map((c) => (
            <span key={c} className="fe-chip">
              {c}
              <button type="button" onClick={() => setPara((p) => p.filter((x) => x !== c))} aria-label={`Quitar ${c}`}><X /></button>
            </span>
          ))}
          <input
            value={escribiendo}
            onChange={(e) => setEscribiendo(e.target.value)}
            onKeyDown={(e) => {
              if (['Enter', ',', ';', ' '].includes(e.key) && escribiendo.trim()) {
                e.preventDefault();
                agregar(escribiendo);
              } else if (e.key === 'Backspace' && !escribiendo && para.length) {
                setPara((p) => p.slice(0, -1));
              }
            }}
            onBlur={() => escribiendo.trim() && agregar(escribiendo)}
            onPaste={(e) => {
              const t = e.clipboardData.getData('text');
              if (/[\s,;]/.test(t)) {
                e.preventDefault();
                agregar(t);
              }
            }}
            placeholder={para.length ? '' : 'correo@ejemplo.com'}
            aria-label="Destinatarios"
            type="text"
            inputMode="email"
            autoComplete="email"
          />
        </div>
      </div>
      <label className="fe-campo-etiquetado">
        <span>Asunto</span>
        <input className="fe-input-caja" value={asunto} onChange={(e) => setAsunto(e.target.value)} />
      </label>
      <label className="fe-campo-etiquetado fe-campo-bloque">
        <span>Mensaje</span>
        <textarea className="fe-area" rows={3} value={mensaje} onChange={(e) => setMensaje(e.target.value)} />
      </label>
      <p className="fe-nota">El correo lleva el enlace al formulario: {url}</p>
      <div className="fe-dialogo-pie">
        <span className="flex-1" />
        <button type="submit" className="fe-btn-primario" disabled={enviando}>
          {enviando ? <Loader2 className="od-spin" /> : <Send />} Enviar
        </button>
      </div>
    </form>
  );
}

function EnviarEnlace({ url, titulo }) {
  const [copiado, setCopiado] = useState(false);
  const texto = encodeURIComponent(titulo || 'Formulario');
  const u = encodeURIComponent(url);
  const redes = [
    { nombre: 'WhatsApp', href: `https://wa.me/?text=${texto}%20${u}` },
    { nombre: 'X', href: `https://twitter.com/intent/tweet?url=${u}&text=${texto}` },
    { nombre: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
  ];
  return (
    <div className="fe-enviar-enlace">
      <div className="fe-enlace-copiar">
        <input readOnly value={url} className="fe-input-caja" aria-label="Enlace del formulario" onFocus={(e) => e.target.select()} />
        <button
          type="button"
          className="fe-btn-primario"
          onClick={async () => {
            if (await copiarTexto(url, 'Enlace copiado')) {
              setCopiado(true);
              setTimeout(() => setCopiado(false), 2000);
            }
          }}
        >
          {copiado ? <Check /> : <Copy />} Copiar
        </button>
      </div>
      <div className="fe-config-rotulo">Compartir en</div>
      <div className="fe-redes">
        {redes.map((r) => (
          <a key={r.nombre} href={r.href} target="_blank" rel="noopener noreferrer" className="fe-btn-borde">{r.nombre}</a>
        ))}
      </div>
    </div>
  );
}

function EnviarInsertar({ url }) {
  const [ancho, setAncho] = useState(640);
  const [alto, setAlto] = useState(800);
  const html = useMemo(
    () => `<iframe src="${url}?embed=1" width="${ancho}" height="${alto}" frameborder="0" marginheight="0" marginwidth="0" title="Formulario">Cargando…</iframe>`,
    [url, ancho, alto],
  );
  return (
    <div className="fe-enviar-insertar">
      <textarea readOnly className="fe-area fe-codigo" rows={4} value={html} aria-label="Código HTML" onFocus={(e) => e.target.select()} />
      <div className="fe-fila-controles">
        <label className="fe-campo-etiquetado">
          <span>Ancho</span>
          <input type="number" min="200" max="2000" className="fe-input-caja fe-input-corto" value={ancho} onChange={(e) => setAncho(Math.max(200, Number(e.target.value) || 640))} />
        </label>
        <label className="fe-campo-etiquetado">
          <span>Alto</span>
          <input type="number" min="200" max="4000" className="fe-input-caja fe-input-corto" value={alto} onChange={(e) => setAlto(Math.max(200, Number(e.target.value) || 800))} />
        </label>
        <span className="flex-1" />
        <button type="button" className="fe-btn-primario" onClick={() => copiarTexto(html, 'Código copiado')}>
          <Copy /> Copiar
        </button>
      </div>
    </div>
  );
}

function EnviarQr({ formId, titulo }) {
  const [src, setSrc] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    let url = '';
    let vivo = true;
    api.get(`/forms/${formId}/qr.png`, { responseType: 'blob' })
      .then(({ data }) => {
        if (!vivo) return;
        url = URL.createObjectURL(data);
        setSrc(url);
      })
      .catch(() => vivo && setError(true));
    return () => {
      vivo = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [formId]);

  return (
    <div className="fe-enviar-qr">
      {src ? <img src={src} alt="Código QR del formulario" /> : error ? <p className="fe-nota">No se pudo generar el código QR.</p> : <Loader2 className="od-spin" />}
      <p className="fe-nota">Imprímelo o ponlo en una presentación: al escanearlo se abre el formulario.</p>
      {src && (
        <a className="fe-btn-borde" href={src} download={`${(titulo || 'formulario').replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'formulario'} (QR).png`}>
          <Download /> Descargar PNG
        </a>
      )}
    </div>
  );
}
